import { Plus, Trash2 } from 'lucide-react';
import { Button } from './Button';
import { TimeSelect } from './TimeRangeInput';

/**
 * v0.4後半 月次実績一括入力 第2フェーズ I-4b (§6-2 項目6): 休憩の内訳 (`break_record_mode='time_range'`
 * の店舗向け、`work_record_breaks` に保存される)。1行ずつの開始・終了時刻 + 「＋休憩を追加」
 * + 「60分」「45分」ボタン + 合計の常時表示。
 *
 * ⚠️ 最低休憩不足の警告 (§6-2) は未実装 (残課題): バックエンドの判定式 (isMinimumBreakInsufficient、
 * lib/work-records.ts) をフロントで再実装するとミラー実装の罠 (§8-134、本日だけで複数の労働時間計算
 * バグを踏んでいる) の再発リスクがあるため、専用の解決APIが無い現時点では合計時間の表示のみに留める。
 * ⚠️ design-expert High反映: ネイティブ `input[type=time]` は `ui-guidelines.md §7.2` で禁止されており
 * (同じシートの `TimeRangeInput` が既に守っている規約)、`TimeRangeInput.tsx` の内部 `TimeSelect`
 * (時/分をそれぞれ `<Select>` で選ぶ) を export して使い回す。
 */
export type BreakRow = { startTime: string | null; endTime: string | null };

type Props = {
  rows: BreakRow[];
  onChange: (rows: BreakRow[]) => void;
  disabled?: boolean;
};

const addMinutes = (hhmm: string, minutes: number): string => {
  const [h, m] = hhmm.split(':').map(Number);
  const total = ((h ?? 0) * 60 + (m ?? 0) + minutes) % (24 * 60);
  const normalized = total < 0 ? total + 24 * 60 : total;
  return `${String(Math.floor(normalized / 60)).padStart(2, '0')}:${String(normalized % 60).padStart(2, '0')}`;
};

const diffMinutes = (start: string, end: string): number => {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const s = (sh ?? 0) * 60 + (sm ?? 0);
  let e = (eh ?? 0) * 60 + (em ?? 0);
  if (e <= s) e += 24 * 60; // 日またぎの休憩 (稀) はそのまま加算 (§8-188型の極端なケースは未対応)
  return e - s;
};

export function BreaksEditor({ rows, onChange, disabled }: Props) {
  const totalMinutes = rows.reduce((sum, r) => sum + (r.startTime && r.endTime ? diffMinutes(r.startTime, r.endTime) : 0), 0);

  const addRow = (durationMinutes: number) => {
    const last = rows[rows.length - 1];
    const startTime = last?.endTime ?? '12:00';
    onChange([...rows, { startTime, endTime: addMinutes(startTime, durationMinutes) }]);
  };
  const updateRow = (i: number, patch: Partial<BreakRow>) => {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const removeRow = (i: number) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-text-sub">休憩</span>
        <span className="text-xs text-text-sub tabular-nums">合計 {totalMinutes}分</span>
      </div>
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-1.5 flex-wrap">
          <TimeSelect label={`休憩${i + 1} 開始時刻`} value={row.startTime} disabled={disabled} onChange={(v) => updateRow(i, { startTime: v })} />
          <span className="text-text-sub" aria-hidden>〜</span>
          <TimeSelect label={`休憩${i + 1} 終了時刻`} value={row.endTime} disabled={disabled} onChange={(v) => updateRow(i, { endTime: v })} />
          <button type="button" aria-label={`休憩${i + 1}を削除`} disabled={disabled} onClick={() => removeRow(i)}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-text-sub hover:text-brand-red shrink-0"
          ><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="flex items-center gap-1.5 flex-wrap">
        <Button type="button" variant="secondary" size="sm" disabled={disabled} leftIcon={<Plus size={12} />} onClick={() => addRow(60)}>60分</Button>
        <Button type="button" variant="secondary" size="sm" disabled={disabled} leftIcon={<Plus size={12} />} onClick={() => addRow(45)}>45分</Button>
        <Button type="button" variant="secondary" size="sm" disabled={disabled} leftIcon={<Plus size={12} />} onClick={() => addRow(0)}>休憩を追加</Button>
      </div>
    </div>
  );
}
